import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header } from '../../components/ui/header/header.js';
import { Footer } from '../../components/ui/footer/footer.js';
import { FluidMouseLight } from '../../components/ui/fluid-light/fluid-light.js';
import { ErrorShakeProvider } from '../../components/ui/error-shake/error-shake-context.js';
import { AnimatedOutlet } from './animated-outlet.js';
import { ToastRegion } from './toast-region.js';

const ROUTE_KEYS: Record<string, string> = {
  '/': 'nav.home',
  '/dashboard': 'nav.dashboard',
  '/blog': 'nav.blog',
  '/docs': 'nav.docs',
  '/changelog': 'nav.changelog',
  '/getting-started': 'nav.installation',
  '/faq': 'nav.faq',
  '/billing': 'nav.billing',
  '/account': 'nav.account',
};

export function AppShell() {
  const location = useLocation();
  const { t } = useTranslation('common');
  const announcerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const pageKey = ROUTE_KEYS[location.pathname];
    const pageName = pageKey ? t(pageKey) : t('page');
    if (announcerRef.current) {
      announcerRef.current.textContent = t('navigatedTo', { page: pageName });
    }
  }, [location.pathname, t]);

  // Scroll back to the top on navigation (the window is the scroll container).
  // Keyed on pathname only so in-page hash anchors (the blog jump cards, the
  // article table of contents) keep their own scroll behavior.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    // `isolate` opens a fresh stacking context here so the FluidMouseLight
    // (z-[-1]) paints ABOVE the wrapper's bg-canvas instead of behind it.
    // Without isolate the canvas bg paints over the orbs and the glow is
    // invisible.
    <div className="isolate flex flex-col min-h-screen bg-canvas">
      <FluidMouseLight />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-3 focus:bg-surface focus:text-primary"
      >
        {t('skipToContent')}
      </a>

      <Header />

      <ErrorShakeProvider>
        {(attach) => {
          // Landing renders its own viewport-spanning hero (swirl dither
          // shader) and each section self-centers, so it must not be wrapped
          // in a max-width container.
          const isEdgeToEdge = location.pathname === '/';
          const isFullBleed =
            !isEdgeToEdge &&
            (location.pathname === '/dashboard' ||
              location.pathname === '/getting-started' ||
              location.pathname === '/billing' ||
              location.pathname === '/account' ||
              location.pathname === '/device' ||
              location.pathname === '/docs' ||
              location.pathname === '/changelog' ||
              location.pathname === '/blog' ||
              location.pathname.startsWith('/blog/'));
          return (
            <main id="main-content" ref={attach} className="flex-1">
              {isEdgeToEdge ? (
                <AnimatedOutlet />
              ) : isFullBleed ? (
                <div className="mx-auto w-full max-w-[1200px]">
                  <AnimatedOutlet />
                </div>
              ) : (
                <div className="mx-auto p-6 max-w-full standard:max-w-[800px] wide:max-w-[1000px] ultrawide:max-w-[1200px]">
                  <AnimatedOutlet />
                </div>
              )}
            </main>
          );
        }}
      </ErrorShakeProvider>

      {/* Pages that ship their own editorial footer — suppress the global legal footer. */}
      {location.pathname !== '/dashboard' && location.pathname !== '/getting-started' && <Footer />}

      <ToastRegion />

      <div ref={announcerRef} aria-live="polite" role="status" className="sr-only" />
    </div>
  );
}
